import { FmButton } from './FmButton';
import { FmHeading, FmStack, FmText } from './FmComposition';
import { FmSplit } from './FmLayout';
import { FmActionGroup } from './FmListPrimitives';
import type { FmDashboardOverviewProps } from './FmDashboardOverview.types';

type HeaderProps = Pick<
  FmDashboardOverviewProps,
  'title' | 'subtitle' | 'introduction' | 'headerActions' | 'handlers'
> & { testId: string };

export function DashboardHeader({
  title,
  subtitle,
  introduction,
  headerActions,
  handlers,
  testId,
}: HeaderProps) {
  const copy = (
    <FmStack gap="xs">
      <FmHeading level={1}>{title}</FmHeading>
      {subtitle ? <FmText tone="subtle">{subtitle}</FmText> : null}
      {introduction ? (
        <FmText
          as="p"
          className="fm-dashboard-overview__introduction"
          data-testid={`${testId}-introduction`}
        >
          {introduction.description}
        </FmText>
      ) : null}
    </FmStack>
  );
  const actions = headerActions ? (
    <div className="fm-dashboard-overview__header-actions">
      {introduction ? (
        <>
          <FmHeading level={2} className="fm-dashboard-overview__action-title">
            {introduction.actionTitle}
          </FmHeading>
          <FmText as="p" className="fm-dashboard-overview__action-hint">
            {introduction.actionHint}
          </FmText>
        </>
      ) : null}
      <FmActionGroup collapseOnMobile data-testid={`${testId}-header-actions`}>
        {headerActions
          .filter((action) => !introduction || action.intent === 'primary')
          .map((action) => (
            <FmButton
              key={action.id}
              loading={action.pending}
              variant={
                action.intent === 'primary'
                  ? 'primary'
                  : action.intent === 'secondary'
                    ? 'text'
                    : 'default'
              }
              size={action.intent === 'primary' ? 'lg' : undefined}
              onClick={() =>
                handlers.onPrimaryAction({ actionId: action.id, jobId: action.jobId })
              }
              data-testid={`${testId}-header-action-${action.id}`}
            >
              {action.label}
            </FmButton>
          ))}
      </FmActionGroup>
    </div>
  ) : null;
  return (
    <header
      className={`fm-dashboard-overview__header${introduction ? ' fm-dashboard-overview__header--introduction' : ''}`}
    >
      {introduction && actions ? (
        /* The introduction reads first; the action card is the split's narrow panel. */
        <FmSplit
          balance="copy-narrow"
          side="start"
          align="center"
          stackOrder="graphic-first"
          contentSurface="plain"
          graphic={copy}
          content={actions}
        />
      ) : (
        <div className="fm-dashboard-overview__header-content">
          {copy}
          {actions}
        </div>
      )}
    </header>
  );
}
